/* =============================================================================
   T3BLOGG_OS — assets/shop-nav.js
   1. Collection-tab swaps inside the shop window (no page nav).
   2. Folder dock interception so all folders open as in-page windows.
   ============================================================================= */

(function () {
  'use strict';
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  function escHTML(s) {
    return String(s || '').replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  // Prices come back as either:
  //  - cents integer  (from /products/X.js)
  //  - "1003.00" string (from /collections/X/products.json)
  function fmtPrice(price) {
    let amount;
    if (typeof price === 'number') amount = price / 100;
    else if (typeof price === 'string') amount = parseFloat(price);
    else return '';
    if (!isFinite(amount)) return '';
    return window.T3Money(amount * 100);
  }

  // /products/X.js returns a top-level product.price (cents int), but
  // /collections/X/products.json (used by the tab swap) does NOT — the price
  // only lives on each variant (as a "1003.00" string). Derive the lowest
  // variant price (as cents) so the card price never renders empty.
  function productPrice(product) {
    if (product.price != null && product.price !== '') return product.price;
    let minCents = null;
    for (const v of (product.variants || [])) {
      let cents;
      if (typeof v.price === 'number') cents = v.price;            // already cents
      else if (typeof v.price === 'string') cents = parseFloat(v.price) * 100;
      else continue;
      if (!isFinite(cents)) continue;
      if (minCents === null || cents < minCents) minCents = cents;
    }
    return minCents;
  }

  // Sold out = no variant available. products.json has no top-level
  // `available`, so fall back to the variants.
  function isAvailable(product) {
    if (typeof product.available === 'boolean') return product.available;
    const variants = product.variants || [];
    return variants.length > 0 && variants.some(v => v.available);
  }

  function imgAt(url, w) {
    if (!url || typeof url !== 'string') return '';
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'width=' + w;
  }

  // Variant options live in two shapes too:
  //  - v.options = ["S"]               (products.js)
  //  - v.option1 / v.option2 / option3 (products.json)
  function variantOption(v, idx) {
    if (!v) return undefined;
    if (Array.isArray(v.options) && idx >= 0 && idx < v.options.length) return v.options[idx];
    if (idx === 0) return v.option1;
    if (idx === 1) return v.option2;
    if (idx === 2) return v.option3;
    return undefined;
  }

  // ---------------------------------------------------------------------------
  // Card renderer (mirrors snippets/product-card-file.liquid)
  // ---------------------------------------------------------------------------
  function buildCard(product) {
    const filename = (product.handle || '').toUpperCase().replace(/-/g, '_') + '.JPG';
    const title    = (product.title  || '').toUpperCase();

    // Images: either array of strings (products.js) or array of objects {src,...} (products.json).
    const imagesArr = product.images || [];
    function imgUrl(entry) {
      if (!entry) return '';
      if (typeof entry === 'string') return entry;
      return entry.src || entry.url || '';
    }
    const firstImgUrl = imgUrl(imagesArr[0]);
    const imgCount    = imagesArr.length;

    // Pick the first option that actually has multiple distinct values.
    let pickerIdx = -1;
    const opts = product.options || [];
    for (let i = 0; i < opts.length; i++) {
      const seen = new Set();
      for (const v of (product.variants || [])) {
        const val = variantOption(v, i);
        if (val) seen.add(val);
      }
      if (seen.size > 1) { pickerIdx = i; break; }
    }

    let totalSizes = 0, soldOut = 0, sizePills = '';

    if (pickerIdx >= 0) {
      const seen = new Set(), order = [];
      (product.variants || []).forEach(v => {
        const sz = variantOption(v, pickerIdx);
        if (sz && !seen.has(sz)) { seen.add(sz); order.push(sz); }
      });
      order.forEach((sz, i) => {
        totalSizes++;
        const isOut = !(product.variants || []).some(v => variantOption(v, pickerIdx) === sz && v.available);
        if (isOut) soldOut++;
        sizePills += `<span class="${isOut ? 'out' : ''}">${escHTML(sz)}</span>`;
        if (i < order.length - 1) sizePills += '<span class="sep">·</span>';
      });
    } else {
      totalSizes = 1;
      const only = (product.variants || [])[0];
      const ok = only ? only.available : product.available;
      if (!ok) soldOut = 1;
      sizePills = `<span class="${ok ? '' : 'out'}">ONE</span>`;
    }

    const fullySold = soldOut >= totalSizes;
    const lowStock  = !fullySold && soldOut >= Math.max(2, Math.ceil(totalSizes * 0.4));

    let badge = '';
    if (fullySold)       badge = '<span class="badge sold">SOLD OUT</span>';
    else if (lowStock)   badge = '<span class="badge">LOW STOCK</span>';

    const thumb = firstImgUrl
      ? `<img src="${imgAt(firstImgUrl, 600)}"
              srcset="${imgAt(firstImgUrl, 300)} 300w, ${imgAt(firstImgUrl, 600)} 600w, ${imgAt(firstImgUrl, 900)} 900w"
              sizes="(max-width: 720px) 50vw, 360px"
              alt="${escHTML(product.title)}" loading="lazy">`
      : `<span class="ph">// PLACEHOLDER<br>1:1<br>${filename}</span>`;

    return `
      <a href="/products/${escHTML(product.handle)}" class="file-card${fullySold ? ' sold' : ''}"
         data-pid="${product.id}" data-handle="${escHTML(product.handle)}"
         aria-label="${escHTML(product.title)}">
        <div class="thumb">${thumb}${badge}</div>
        <div class="name">${title}</div>
        <div class="sizes-list">${sizePills}</div>
        <div class="price">${fmtPrice(productPrice(product))}</div>
      </a>`;
  }

  // ---------------------------------------------------------------------------
  // Collection swap
  // ---------------------------------------------------------------------------
  async function loadCollection(handle, activeBtn) {
    const wrap = $('#win-shop-grid');
    if (!wrap) return;

    const countEl = wrap.querySelector('[data-shop-count]');
    const titleEl = wrap.querySelector(':scope > .bar > .title');
    const pathEl  = wrap.querySelector(':scope > .path');

    $$('.collection-tab', wrap).forEach(t => t.classList.toggle('on', t === activeBtn));

    let grid = wrap.querySelector('.file-grid');
    if (!grid) {
      grid = document.createElement('div');
      grid.className = 'file-grid';
      // Append to body (after shop-tools)
      const body = wrap.querySelector(':scope > .body');
      if (body) body.appendChild(grid);
      else return;
    }
    grid.innerHTML = `
      <div class="shop-loader" role="status" aria-live="polite">
        <div class="shop-loader-bar"><div class="fill"></div></div>
        <div class="shop-loader-text">&gt; indexing ${escHTML(handle)}<span class="cur">_</span></div>
      </div>
    `;

    try {
      const r = await fetch(`/collections/${handle}/products.json?limit=250`, { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error('fetch failed (' + r.status + ')');
      const data = await r.json();
      const products = (data.products || []).slice();
      // In-stock first, sold-out at the bottom (stable sort preserves order within groups).
      products.sort((a, b) => (isAvailable(a) ? 0 : 1) - (isAvailable(b) ? 0 : 1));

      if (products.length === 0) {
        grid.innerHTML = `<div style="grid-column:1/-1;padding:36px;color:var(--deep-green)"><b style="color:var(--phosphor)">// empty</b><br><br>No files indexed.</div>`;
      } else {
        grid.innerHTML = products.map(buildCard).join('');
      }

      if (location.pathname.startsWith('/collections/')) {
        try { history.replaceState(history.state, '', activeShopUrl()); } catch (e) {}
      }
      if (countEl) countEl.textContent = `${products.length} files · ${handle}`;
      if (titleEl) titleEl.textContent = `~/shop/${handle}`;
      if (pathEl)  pathEl.textContent  = `root@t3blogg:~/shop/${handle} $ ls`;
      document.dispatchEvent(new CustomEvent('syslog', { detail: { text: `SWAPPED collection // ${handle}` } }));
    } catch (e) {
      grid.innerHTML = `<div style="grid-column:1/-1;padding:36px;color:var(--alert);font-size:12px">! LOAD_FAILED: ${escHTML(e.message || 'unknown error')}</div>`;
    }
  }

  document.addEventListener('click', e => {
    const tab = e.target.closest('.collection-tab');
    if (!tab) return;
    if (tab.classList.contains('on')) return;
    e.preventDefault();
    loadCollection(tab.dataset.handle, tab);
  });

  // ---------------------------------------------------------------------------
  // Folder click interception (CMS pages + shop focus)
  // ---------------------------------------------------------------------------
  function isMobile() { return matchMedia('(max-width: 720px)').matches; }

  // On mobile only one folder window is fullscreen at a time, on top of the shop
  // which is the persistent landing. Close other folder windows but keep the shop.
  function closeOtherWindowsOnMobile(except) {
    if (!isMobile()) return;
    $$('.section-floating-folder > .win').forEach(w => {
      if (w !== except && !w.classList.contains('minimized')) w.classList.add('minimized');
    });
    $$('#desktop > .win').forEach(w => {
      if (w !== except) w.remove();
    });
  }

  // The shop window's shareable URL is the collection of its active tab.
  function activeShopUrl() {
    const tab = document.querySelector('#win-shop-grid .collection-tab.on');
    const handle = (tab && tab.dataset.handle) || 'all';
    return '/collections/' + encodeURIComponent(handle);
  }

  function focusEmbeddedWindow(selector) {
    const win = $(selector);
    if (!win) return;
    closeOtherWindowsOnMobile(win);
    win.classList.remove('minimized');
    if (window.T3Desktop && window.T3Desktop.focusWin) window.T3Desktop.focusWin(win);

    // Update the URL so it can be shared. Reloading the URL lands the recipient
    // on the matching standalone page template.
    const urlMap = {
      'win-video-folder':   '/pages/video',
      'win-image-folder':   '/pages/lookbook',
      'win-returns-folder': '/pages/returns',
      'win-contact-folder': '/pages/contact',
      'win-shop-grid':      activeShopUrl()
    };
    if (urlMap[win.id]) {
      try { history.pushState({ t3: 'folder', id: win.id }, '', urlMap[win.id]); } catch (e) {}
    }
  }

  // FILES dropdown (mobile) — the top-left trigger toggles the folder dock,
  // which is styled as a dropdown panel on small screens.
  function initFilesDropdown() {
    const trigger = document.getElementById('files-trigger');
    const dock = document.querySelector('.dock');
    if (!trigger || !dock) return;
    trigger.addEventListener('click', e => {
      e.stopPropagation();
      const open = dock.classList.toggle('files-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    // Close after choosing a folder, or when tapping outside.
    dock.addEventListener('click', e => {
      if (e.target.closest('.folder')) {
        dock.classList.remove('files-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('click', e => {
      if (dock.classList.contains('files-open') && !dock.contains(e.target) && e.target !== trigger) {
        dock.classList.remove('files-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilesDropdown);
  } else {
    initFilesDropdown();
  }

  async function openPage(handle) {
    const desktop = $('#desktop');
    if (!desktop) return;

    const existingId = 'win-page-' + handle;
    const existing = document.getElementById(existingId);
    if (existing) {
      existing.classList.remove('minimized');
      if (window.T3Desktop && window.T3Desktop.focusWin) window.T3Desktop.focusWin(existing);
      return;
    }

    try {
      const r = await fetch(`/pages/${handle}?section_id=main`, { headers: { Accept: 'text/html' } });
      if (!r.ok) throw new Error('page fetch failed: ' + r.status);
      const html = await r.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const winEl = doc.querySelector('.win');
      if (!winEl) throw new Error('no window markup found');

      winEl.dataset.closeAction = 'remove';
      winEl.removeAttribute('data-navigable');
      winEl.removeAttribute('data-close-target');
      winEl.removeAttribute('data-bound');

      const dRect = desktop.getBoundingClientRect();
      const w = Math.min(720, dRect.width - 60);
      const h = Math.min(560, dRect.height - 60);
      const cx = (dRect.width - w) / 2 + (Math.random() * 60 - 30);
      const cy = (dRect.height - h) / 2 + (Math.random() * 40 - 20);
      winEl.style.width = w + 'px';
      winEl.style.height = h + 'px';
      winEl.style.left = Math.max(24, Math.min(dRect.width - w - 24, cx)) + 'px';
      winEl.style.top  = Math.max(16, Math.min(dRect.height - h - 16, cy)) + 'px';

      desktop.appendChild(winEl);
      if (window.T3Desktop) {
        window.T3Desktop.bindWindow(winEl);
        if (window.T3Desktop.focusWin) window.T3Desktop.focusWin(winEl);
      }
      if (matchMedia('(max-width: 720px)').matches) {
        try { winEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (err) { winEl.scrollIntoView(); }
      }
      document.dispatchEvent(new CustomEvent('syslog', { detail: { text: `OPENED /pages/${handle}` } }));
    } catch (e) {
      openPlaceholder(handle, e.message || 'fetch failed');
    }
  }

  function openPlaceholder(handle, errMsg) {
    const desktop = $('#desktop');
    if (!desktop) return;

    const winEl = document.createElement('div');
    winEl.className = 'win dark no-pad';
    winEl.id = 'win-placeholder-' + handle;
    winEl.dataset.closeAction = 'remove';
    winEl.innerHTML = `
      <div class="bar">
        <span class="title">!! /pages/${handle}</span>
        <span class="dots">
          <button class="min" type="button" aria-label="minimize">_</button>
          <button class="close" type="button" aria-label="close">×</button>
        </span>
      </div>
      <div class="path">root@t3blogg:~ $ cat /pages/${handle}</div>
      <div class="body">
        <div class="t-404" style="padding:24px">
          <h1>PAGE_NOT_CONFIGURED</h1>
          <div class="stack">PATH : /pages/${handle}
CODE : 404
MSG  : page does not exist in shopify admin

# TO FIX:
1. open shopify admin → online store → pages
2. click "add page"
3. set the page handle to "${handle}"
4. (optional) assign a matching theme template
5. publish</div>
        </div>
      </div>
      <div class="resize" aria-hidden="true"></div>
    `;
    const dRect = desktop.getBoundingClientRect();
    const w = Math.min(560, dRect.width - 60);
    const h = Math.min(420, dRect.height - 60);
    winEl.style.width  = w + 'px';
    winEl.style.height = h + 'px';
    winEl.style.left   = Math.max(24, (dRect.width  - w) / 2) + 'px';
    winEl.style.top    = Math.max(16, (dRect.height - h) / 2) + 'px';

    desktop.appendChild(winEl);
    if (window.T3Desktop) {
      window.T3Desktop.bindWindow(winEl);
      if (window.T3Desktop.focusWin) window.T3Desktop.focusWin(winEl);
    }
    if (matchMedia('(max-width: 720px)').matches) {
      try { winEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (err) { winEl.scrollIntoView(); }
    }
    document.dispatchEvent(new CustomEvent('syslog', { detail: { text: `! /pages/${handle} NOT_CONFIGURED`, level: 'warn' } }));
  }

  document.addEventListener('click', e => {
    const folder = e.target.closest('.folder');
    if (!folder) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    // Mobile: let folders navigate to their real pages (SHOP→/, VIDEO→/pages/video,
    // etc.) instead of opening in-page windows. Desktop keeps the window behaviour.
    if (isMobile()) return;

    const href = folder.getAttribute('href') || '';
    if (!href || href === '#') return;

    let url;
    try { url = new URL(href, location.origin); } catch (err) { return; }
    if (url.origin !== location.origin) return;

    const path = url.pathname;

    // SHOP / collections → focus the shop window if it's on this page (homepage).
    // Otherwise (standalone page) let the link navigate to the homepage.
    if (path === '/' || path === '' || path === '/collections' || path.startsWith('/collections/')) {
      if (document.querySelector('#win-shop-grid')) {
        e.preventDefault();
        focusEmbeddedWindow('#win-shop-grid');
      }
      return;
    }

    // CMS-style pages → focus the embedded window if present (homepage);
    // otherwise let the link navigate to the standalone page (which has the dock too).
    if (path.startsWith('/pages/')) {
      const handle = path.replace(/^\/pages\//, '').replace(/\/$/, '');
      const embeddedMap = {
        video:    '#win-video-folder',
        lookbook: '#win-image-folder',
        returns:  '#win-returns-folder',
        contact:  '#win-contact-folder'
      };
      if (embeddedMap[handle] && document.querySelector(embeddedMap[handle])) {
        e.preventDefault();
        focusEmbeddedWindow(embeddedMap[handle]);
        return;
      }
      // Homepage but page not embedded → open as a window without leaving.
      if (document.getElementById('win-shop-grid')) {
        e.preventDefault();
        openPage(handle);
      }
      // else: standalone page → navigate normally.
      return;
    }
  });

  window.T3ShopNav = { loadCollection, openPage };
})();
